import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "../../components/Grid/Grid";
import { CUSTOM_MESSAGES, PAGE_SIZES } from "../../constants/globalConstants";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import MainHeading from "../../components/MainHeading/MainHeading";
import CustomButton from "../../components/CustomButton/CustomButton";
import ThreeDotActionItem from "../../components/ThreeDotActionItem/ThreeDotActionItem";
import useGridHeight from "../../hooks/useGridHeight";
import { useLazyGetModelListQuery } from "../../app/api/modelApi";
import { useLazyGetFamilyListQuery } from "../../app/api/familyApi";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import { useLazyGetTemplateListQuery } from "../../app/api/templateApi";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useFilterManager } from "../../hooks/useFilterManager";
import { useAppSelector } from "../../redux/store";

const TemplatePortal = () => {
  const { handleFilterChange } = useFilterManager();
  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );
  const [tableData, setTableData] = useState<any[]>([]);
  const [brandId, setBrandId] = useState<string | null>(
    persistDropdownSelector["brand_selector"]?.value ?? null
  );
  const [familyId, setFamilyId] = useState<string | null>(
    persistDropdownSelector["family_selector"]?.value ?? null
  );
  const [modelId, setModelId] = useState<string | null>(
    persistDropdownSelector["model_selector"]?.value ?? null
  );

  const navigate = useNavigate();
  useGridHeight();
  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [getFamilyListQuery] = useLazyGetFamilyListQuery();
  const [getModelListQuery] = useLazyGetModelListQuery();
  const [getTemplateListQuery, templateListResponse] =
    useLazyGetTemplateListQuery();

  const pageSizeRef = useRef(String(PAGE_SIZES.Default_List_Size));
  const tableHeaders = [
    {
      headerName: "Name",
      shouldSort: true,
    },
    {
      headerName: "Unique ID",
      shouldSort: true,
    },
    {
      headerName: "Brand Name",
      shouldSort: true,
    },
    {
      headerName: "Family Name",
      shouldSort: true,
    },
    {
      headerName: "Model Name",
      shouldSort: true,
    },
    {
      headerName: "Firmware Version",
      shouldSort: true,
    },
    {
      headerName: "Status",
    },
    { headerName: "" },
  ];

  const asyncFormSelectProps = [
    {
      label: "Brand",
      name: "brand_name",
      onChange: (val: string | null, label: string) => {
        if (val !== brandId) {
          setBrandId(val);
          setFamilyId(null);
          setModelId(null);
          handleFilterChange({
            updatedField: "brand_id",
            persistedSelection: {
              label,
              value: val,
            },
          });
        }
      },
      fetchQuery: getBrandListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
      },
      optionLabelName: "brand_name",
      placeholder: "Select Brand",
      shouldFetch: true,
      persistedOption: persistDropdownSelector["brand_selector"]
        ? {
            label: persistDropdownSelector["brand_selector"]?.label,
            value: persistDropdownSelector["brand_selector"]?.value,
          }
        : null,
    },
    {
      label: "Family",
      name: "family_name",
      onChange: (val: string | null, label: string) => {
        if (val !== familyId) {
          setFamilyId(val);
          setModelId(null);
          handleFilterChange({
            updatedField: "family_id",
            persistedSelection: {
              label,
              value: val,
            },
          });
        }
      },
      fetchQuery: getFamilyListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
      },
      optionLabelName: "family_name",
      placeholder: "Select Family",
      key: brandId,
      shouldFetch: brandId?.length,
      persistedOption: persistDropdownSelector["family_selector"]
        ? {
            label: persistDropdownSelector["family_selector"]?.label,
            value: persistDropdownSelector["family_selector"]?.value,
          }
        : null,
    },
    {
      label: "Model",
      name: "model_name",
      onChange: (val: string | null, label: string) => {
        setModelId(val);
        handleFilterChange({
          updatedField: "model_id",
          persistedSelection: {
            label,
            value: val,
          },
        });
      },
      fetchQuery: getModelListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
        family_id: familyId,
      },
      optionLabelName: "model_name",
      placeholder: "Select Model",
      key: familyId,
      shouldFetch: brandId?.length && familyId?.length,
      persistedOption: persistDropdownSelector["model_selector"]
        ? {
            label: persistDropdownSelector["model_selector"]?.label,
            value: persistDropdownSelector["model_selector"]?.value,
          }
        : null,
    },
  ];

  const handleGetTemplateList = async () => {
    try {
      const response = await getTemplateListQuery({
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
        family_id: familyId,
        model_id: modelId,
      }).unwrap();

      if (response?.status_code === 200) {
        setTableData(response?.data);
      }
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Fetch Template List Error",
      });
    }
  };

  useEffect(() => {
    if (brandId && familyId && modelId) {
      handleGetTemplateList();
    } else {
      setTableData([]);
    }
    // eslint-disable-next-line
  }, [brandId, familyId, modelId]);

  const updatedTableData = (tableData ?? []).map((tdata) => ({
    Name: tdata.template_name,
    "Unique ID": tdata.id,
    "Brand Name": tdata.brand_name,
    "Family Name": tdata?.family_name,
    "Model Name": tdata?.model_name,
    "Provisioning URL": tdata?.firmware_version,
    Status: [
      tdata.is_active
        ? { text: "Active", color: "#12da85" }
        : { text: "Inactive", color: "#bbbbbb" },
    ],
    actionIcon: (
      <ThreeDotActionItem
        actionItemsMenuPositionStyles={{
          right: "15px",
          left: "unset",
        }}
        actionIconSubItemsList={[
          {
            actionItemName: "View Details",
            onClick: (id: string) =>
              navigate(`/provisioning/template/create?id=${tdata.id}`),
          },
          {
            actionItemName: "Edit Details",
            onClick: (id: string) =>
              navigate(`/provisioning/template/edit?id=${tdata.id}`),
          },
        ]}
        id={tdata.id}
      />
    ),
  }));

  const handleLoadMoreClick = async () => {
    try {
      if (
        templateListResponse.isSuccess &&
        templateListResponse.data?.next_start_key
      ) {
        const response = await getTemplateListQuery(
          {
            startKey: templateListResponse.data?.next_start_key ?? "",
            page_size: pageSizeRef.current,
            brand_id: brandId,
            family_id: familyId,
            model_id: modelId,
          },
          true
        ).unwrap();

        if (response && response?.data?.data) {
          setTableData((prev) => [...prev, ...response?.data?.data]);
        }
      }
    } catch (error: any) {
      console.error(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Load More Template List Error",
      });
    }
  };

  return (
    <>
      <Layout>
        <MainHeading headerText="Template" subHeaderText={"Description"}>
          <CustomButton
            isSubmitting={false}
            label="Create Template"
            customClass="w-11rem px-1 py-0 m-0 h-48 fs-6"
            onClick={() => navigate("create")}
            disabled={false}
          />
        </MainHeading>

        <Grid
          tableHeaders={tableHeaders}
          tableData={updatedTableData}
          isLoading={templateListResponse.isLoading}
          isDataPresent={tableData?.length > 0}
          isError={templateListResponse.isError}
          error={templateListResponse.error}
          noDataUIMainText={CUSTOM_MESSAGES.No_Template_Yet}
          noDataUISubText={CUSTOM_MESSAGES.Create_Template_Now}
          noDataUIButtonLabel="Create Template"
          showPageSizeSelector
          page_size={pageSizeRef.current}
          handlePageSizeChange={(val) => {
            if (val !== pageSizeRef.current) {
              pageSizeRef.current = val as string;
              handleGetTemplateList();
            }
          }}
          sort={{ sortField: "Name", sortOrder: "asc" }}
          handleClick={() => navigate("create")}
          asyncFormSelectProps={asyncFormSelectProps}
        />

        {((templateListResponse.isSuccess &&
          templateListResponse?.data?.next_start_key) ||
          templateListResponse.isFetching) && (
          <div className="text-center" id="loadmore-button">
            <CustomButton
              isSubmitting={templateListResponse.isFetching}
              label="Load More"
              customClass="px-1 mw-100 py-0 m-0 mt-3 fs-6"
              customStyles={{ width: "30rem" }}
              onClick={handleLoadMoreClick}
            />
          </div>
        )}
      </Layout>
    </>
  );
};

export default TemplatePortal;
